/**
 * Termine bestaetigen (F-29): stattgefunden, ausgefallen oder anders gelaufen.
 *
 * Rein, ohne Datenbank. Zeitbloecke von Aufgaben gelten automatisch als
 * stattgefunden, wenn die Aufgabe abgehakt ist, sonst nach ihrem Ende als
 * ausgefallen - eine ausdrueckliche Bestaetigung geht immer vor.
 * Trainingstermine (F-34 E) ebenso nach dem Protokoll: fertig mit Uhrzeiten =
 * „anders gelaufen“ mit der tatsaechlichen Zeit, ohne Protokoll ausgefallen.
 * Ganztaegige Termine (Ferien, Geburtstage) brauchen keine Bestaetigung.
 */
import { datePart, differenceInMinutes } from '@/lib/datetime';
import type { CalendarOccurrence, LocalDate, LocalDateTime, TerminStatus } from '@/domain/types';

/** `geplant` = noch nicht vorbei; `offen` = vorbei, aber unbestaetigt. */
export type Zustand = TerminStatus | 'geplant' | 'offen' | 'keine';

export const STATUS_LABEL: Record<TerminStatus, string> = {
  stattgefunden: 'Stattgefunden',
  ausgefallen: 'Ausgefallen',
  anders: 'Anders gelaufen',
};

/** Vorschlaege fuer den Grund bei "ausgefallen" (Vorschlag, frei aenderbar). */
export const GRUENDE = ['krank', 'verschoben', 'abgesagt', 'keine Lust'];

type Occ = Pick<CalendarOccurrence, 'eventId' | 'isRecurring' | 'originalStart' | 'seriesId' | 'start' | 'end' | 'allDay'>;

/** Stabiler Schluessel eines Vorkommens - unabhaengig von Verschieben und Ausnahmen. */
export function bestaetigungsKey(o: Occ): string {
  return o.seriesId && o.originalStart ? `${o.seriesId}::${o.originalStart}` : o.eventId;
}

export function bestaetigbar(o: Pick<CalendarOccurrence, 'allDay'>): boolean {
  return !o.allDay;
}

export function zustand(
  o: Occ & Pick<CalendarOccurrence, 'bestaetigung' | 'sourceTaskId' | 'blockErledigt'> & { training?: unknown; blockZeit?: unknown },
  jetzt: LocalDateTime,
): { zustand: Zustand; automatisch: boolean } {
  if (!bestaetigbar(o)) return { zustand: 'keine', automatisch: false };
  if (o.bestaetigung) return { zustand: o.bestaetigung.status, automatisch: false };
  const vorbei = String(o.end) <= jetzt;
  if ((o.sourceTaskId || o.training) && o.blockErledigt !== undefined) {
    if (o.blockErledigt) return { zustand: o.blockZeit ? 'anders' : 'stattgefunden', automatisch: true };
    return { zustand: vorbei ? 'ausgefallen' : 'geplant', automatisch: true };
  }
  return { zustand: vorbei ? 'offen' : 'geplant', automatisch: false };
}

/**
 * Die Zeit, die tatsaechlich verbracht wurde - fuer die Zeitverteilung
 * (F-28). Ausgefallen: keine. Unbestaetigt zaehlt vorlaeufig wie geplant.
 */
export function tatsaechlicheZeit(
  o: Occ & Pick<CalendarOccurrence, 'bestaetigung' | 'sourceTaskId' | 'blockErledigt' | 'training' | 'blockZeit'>,
  jetzt: LocalDateTime,
): { start: LocalDateTime; end: LocalDateTime } | null {
  const { zustand: z } = zustand(o, jetzt);
  if (z === 'ausgefallen' || z === 'keine') return null;
  if (z === 'anders' && o.bestaetigung?.start && o.bestaetigung.end) return { start: o.bestaetigung.start, end: o.bestaetigung.end };
  if (z === 'anders' && !o.bestaetigung && o.blockZeit) return o.blockZeit;
  return { start: o.start as LocalDateTime, end: o.end as LocalDateTime };
}

/** Vergangene, noch unbestaetigte Termine ab `ab`, aelteste zuerst. */
export function unbestaetigt<T extends Occ & Pick<CalendarOccurrence, 'bestaetigung' | 'sourceTaskId' | 'blockErledigt'>>(
  liste: T[],
  ab: LocalDate,
  jetzt: LocalDateTime,
): T[] {
  return liste
    .filter((o) => datePart(String(o.start)) >= ab && zustand(o, jetzt).zustand === 'offen')
    .sort((a, b) => String(a.start).localeCompare(String(b.start)));
}

export interface AusfallAuswertung {
  stattgefunden: number;
  anders: number;
  ausgefallen: { o: CalendarOccurrence; grund: string }[];
  /** Gruende mit Anzahl, haeufigste zuerst; ohne Angabe als "ohne Grund". */
  gruende: { grund: string; anzahl: number }[];
  /** Ausgefallene Zeit in Minuten (wall clock). */
  ausgefalleneMinuten: number;
}

const OHNE_GRUND = 'ohne Grund';

/**
 * Auswertung fuer den Rueckblick (F-29, Vorschlag): Was fand statt, was fiel
 * aus und warum. Gezaehlt wird nur ausdruecklich Bestaetigtes.
 */
export function ausfallAuswertung(liste: CalendarOccurrence[]): AusfallAuswertung {
  const bestaetigt = liste.filter((o) => !o.allDay && o.bestaetigung);
  const ausgefallen = bestaetigt
    .filter((o) => o.bestaetigung!.status === 'ausgefallen')
    .map((o) => ({ o, grund: o.bestaetigung!.grund?.trim() || OHNE_GRUND }))
    .sort((a, b) => String(a.o.start).localeCompare(String(b.o.start)));
  const zaehler = new Map<string, { grund: string; anzahl: number }>();
  for (const { grund } of ausgefallen) {
    const k = grund.toLocaleLowerCase('de');
    const alt = zaehler.get(k);
    if (alt) alt.anzahl++; else zaehler.set(k, { grund, anzahl: 1 });
  }
  return {
    stattgefunden: bestaetigt.filter((o) => o.bestaetigung!.status === 'stattgefunden').length,
    anders: bestaetigt.filter((o) => o.bestaetigung!.status === 'anders').length,
    ausgefallen,
    gruende: [...zaehler.values()].sort((a, b) => b.anzahl - a.anzahl || a.grund.localeCompare(b.grund, 'de')),
    ausgefalleneMinuten: ausgefallen.reduce((s, { o }) => s + Math.max(0, differenceInMinutes(o.start as LocalDateTime, o.end as LocalDateTime)), 0),
  };
}

